import { useEffect, useMemo, useState } from "react";

import {
  TextArea,
  TextField,
  Select,
  Modal,
} from "@king-ict/eupisi-ui/components";
import {
  codeBookToSelectData,
  hrDateWithTime,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import {
  deleteCommitteeDecision,
  getCommitteeDecision,
  saveCommitteeDecision,
} from "../../api/Request/RequestApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  RequestHeader,
} from "../../components";
import { useAuth } from "../../hooks";
import { ValidationFailure } from "../../models";
import { CommitteeTypeEnum } from "../../models/Committee";
import {
  Request,
  RequestStatus,
  RequestType,
  CommitteeLanguageDecision,
  CommitteeEnrollmentDecision,
  CommitteeDecision,
} from "../../models/Request";
import {
  committeeDecisionDetailsUrl,
  requestAdminDetailsUrl,
} from "../../routes/routes";
import {
  resetIsEdited,
  setIsEdited,
  setSaveOK,
} from "../../slices/applicationSlice";
import { RuleUtils, FormatUtils, MsgUtils } from "../../utils";
import { hasAllowedRoleForEditing } from "./CommitteeDecision";

export function CommitteeDecisionEdit() {
  const { committeeDecisionId, requestId, committeeEmployeeId } = useParams<{
    committeeDecisionId: string;
    requestId: string;
    committeeEmployeeId: string;
  }>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const createDefault = (): Request => {
    return {
      id: 0,
      requestUID: "",
      code: "",
      statusId: RequestStatus.Draft,
      schoolYearId: 0,
      type: RequestType.RegularEnrollment,
      createdByAdmin: true,
      isOutsideEnrollmentArea: false,
      step: 0,
      subStep: 0,
    };
  };
  const createDefaultDecision = (): CommitteeDecision => {
    return {
      id: 0,
      requestId: 0,
      committeeEmployeeId: 0,
      committeeEnrollmentDecisionId: CommitteeEnrollmentDecision.Ready,
      updatedDate: new Date(),
      dateCreated: new Date(),
      hasDecisionsInPreviousInstitutionCommittees: null,
      hasDecisionsInPreviousCountyCommittees: null,
    };
  };
  const [item, setItem] = useState<Request>(createDefault());
  const [croatianTeacher, setCroatianTeacher] = useState(false);
  const [initialData, setInitialData] = useState<CommitteeDecision>(
    createDefaultDecision()
  );

  const [committeeDecision, setCommitteeDecision] = useState<CommitteeDecision>(
    createDefaultDecision()
  );

  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [showOpinionExistsModal, setShowOpinionExistsModal] = useState(false);

  useEffect(() => {
    (async () => {
      if (committeeDecisionId && requestId && committeeEmployeeId) {
        const result = await getCommitteeDecision(
          +committeeDecisionId,
          +requestId,
          +committeeEmployeeId
        );

        if (result) {
          setInitialData(result);
          setCommitteeDecision(result);

          const isCountyCommittee =
            result.committeeEmployee?.committeeRole?.committeeType ===
            CommitteeTypeEnum.County;

          setShowOpinionExistsModal(
            isCountyCommittee
              ? !!result.hasDecisionsInPreviousCountyCommittees
              : !!result.hasDecisionsInPreviousInstitutionCommittees
          );

          if (result.request) setItem(result.request);
          setCroatianTeacher(
            RuleUtils.isCroatianTeacherRole(
              result.committeeEmployee?.committeeRole?.id
            )
          );
        }
      }
      dispatch(setIsEdited());
    })();
  }, [committeeDecisionId, requestId, committeeEmployeeId]);

  const handleRemarkChanged = (val?: string) => {
    const d: CommitteeDecision = {
      ...committeeDecision,
      remark: val,
    };
    handleUpdate(d);
  };

  const handleEnrollmentDecisionChanged = (val: number) => {
    if (!isNaN(val)) {
      const d: CommitteeDecision = {
        ...committeeDecision,
        committeeEnrollmentDecisionId: val,
      };
      handleUpdate(d);
    }
  };
  const handleLanguageDecisionChanged = (val: number) => {
    const d: CommitteeDecision = {
      ...committeeDecision,
      committeeLanguageDecisionId: !isNaN(val) ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleUpdate = (item: CommitteeDecision) => {
    setCommitteeDecision(item);
    dispatch(setIsEdited());
  };
  const handleSaveClicked = async () => {
    const result = await saveCommitteeDecision(committeeDecision);
    if (result) {
      setErrors(result.validationFailures);
      if (result.isValid && result.result) {
        dispatch(resetIsEdited());
        dispatch(setSaveOK());
        history.replace({
          pathname: committeeDecisionDetailsUrl?.(
            result.result?.id,
            Number(requestId),
            Number(committeeEmployeeId)
          ),
        });
      }
    }
  };

  const isAllowedToDelete = useMemo(() => {
    const isRoleAllowed = hasAllowedRoleForEditing(
      initialData.committeeEmployee?.committeeRole?.committeeType ||
        CommitteeTypeEnum.Institution,
      auth
    );

    return isRoleAllowed;
  }, [auth, initialData.committeeEmployee?.committeeRole?.committeeType]);

  const handleDeleteClicked = async () => {
    if (!committeeDecision.id) return;

    try {
      await deleteCommitteeDecision(committeeDecision.id);

      history.push(requestAdminDetailsUrl(+requestId));
    } catch (error) {
      setErrors([
        { propertyName: "delete", message: t("committeeDecision.deleteError") },
      ]);
    }
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={t("committeeDecision.committeeDecisionPageTitle")}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard>
        <Row>
          <Col md={10}>
            <TextArea
              ariaLabel={t("committeeDecision.remark")}
              label={t("committeeDecision.remark")}
              value={committeeDecision?.remark || ""}
              maxLength={4000}
              onChange={(e) => handleRemarkChanged(e.target.value)}
              rows={10}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("Remark"))
                  ?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <div
              style={{ display: "flex", gap: "0.3rem", alignItems: "flex-end" }}
            >
              <TextField
                label={t("committeeDecision.committeeEmployee")}
                value={`${committeeDecision?.committeeEmployee?.employee?.name}, ${committeeDecision?.committeeEmployee?.committeeRole.name}`}
                plaintext
              />
              {!!committeeDecision?.committeeEmployee?.deactivationDate && (
                <TextField
                  value={`- aktivan do ${FormatUtils.formatDate(
                    committeeDecision?.committeeEmployee?.deactivationDate
                  )}`}
                  plaintext
                  bold
                />
              )}
            </div>
          </Col>
          {committeeDecision?.committeeEmployee?.committeeId !==
            item.institutionCommitteeId && (
            <Col md={6}>
              <TextField
                label={t("committeeDecision.committeePosition")}
                value={
                  committeeDecision?.committeeEmployee?.committeePosition?.name
                }
                plaintext
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col md={4}>
            <Select
              label={t("committeeDecision.committeeEnrollmentDecision")}
              value={committeeDecision.committeeEnrollmentDecisionId}
              onChange={(e) =>
                handleEnrollmentDecisionChanged(Number.parseInt(e.target.value))
              }
              selectData={codeBookToSelectData(
                MsgUtils.getEnumToCodeBook(
                  CommitteeEnrollmentDecision,
                  "committeeEnrollmentDecision"
                )
              )}
              errorText={
                errors?.find((e) =>
                  e.propertyName?.startsWith("CommitteeEnrollmentDecisionId")
                )?.message
              }
            />
          </Col>
        </Row>
        {croatianTeacher && (
          <Row>
            <Col md={6}>
              <Select
                label={t("committeeDecision.committeeLanguageDecision")}
                value={committeeDecision.committeeLanguageDecisionId}
                onChange={(e) =>
                  handleLanguageDecisionChanged(Number.parseInt(e.target.value))
                }
                selectData={codeBookToSelectData(
                  MsgUtils.getEnumToCodeBook(
                    CommitteeLanguageDecision,
                    "committeeLanguageDecision"
                  )
                )}
                emptyOption={{ text: "", value: undefined }}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("CommitteeLanguageDecisionId")
                  )?.message
                }
              />
            </Col>
          </Row>
        )}
        {committeeDecision && committeeDecision.id !== 0 && (
          <>
            <Row>
              <Col md={12}>
                <TextField
                  label={t("committeeDecision.createdDateTimeUser")}
                  value={
                    committeeDecision !== undefined
                      ? hrDateWithTime(committeeDecision.dateCreated) +
                        (committeeDecision.createdAppUser != undefined
                          ? ", " +
                            committeeDecision.createdAppUser?.firstName +
                            " " +
                            committeeDecision.createdAppUser?.lastName
                          : "")
                      : ""
                  }
                  plaintext
                />
              </Col>
            </Row>
            {committeeDecision.dateUpdated && (
              <Row>
                <Col md={12}>
                  <TextField
                    label={t("committeeDecision.lastChangedDateTimeUser")}
                    value={
                      committeeDecision !== undefined
                        ? hrDateWithTime(committeeDecision.dateUpdated) +
                          (committeeDecision.appUser != undefined
                            ? ", " +
                              committeeDecision.appUser?.firstName +
                              " " +
                              committeeDecision.appUser?.lastName
                            : "")
                        : ""
                    }
                    plaintext
                  />
                </Col>
              </Row>
            )}
          </>
        )}
        {isAllowedToDelete && !!initialData?.remark && (
          <Button variant="outline-danger" onClick={handleDeleteClicked}>
            {t("common.delete")}
          </Button>
        )}
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={"show"}
        onClickSave={handleSaveClicked}
      />
      <Modal
        type="message"
        confirmText={t("common.close")}
        open={showOpinionExistsModal}
        onConfirm={() => setShowOpinionExistsModal(false)}
        onClose={() => setShowOpinionExistsModal(false)}
      >
        {t("committeeDecision.opinionExistsMessage")}
      </Modal>
    </ContentArea>
  );
}
